// src/components/Login.js - WITH GDPR COMPLIANCE
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Lock, Mail, AlertCircle, CheckCircle, User, Shield, FileText,X } from 'lucide-react';
import EmailVerificationStatus from './EmailVerificationStatus';
import logo from '../assets/logo.png';
import '../styles/LoginNew.css'
import { trackFunnelStep } from '../utils/conversionTracking';
import { trackEvent } from '../utils/analytics';
import { useEnhancedDeviceDetection } from './useEnhancedDeviceDetection';

const Login = ({ isAdminLogin = false, isSignUp = false }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    gender: '',
    // GDPR consent fields
    ageConfirmed: false,
    termsAccepted: false,
    marketingConsent: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showGoogleCompleteModal, setShowGoogleCompleteModal] = useState(false);
  const [googleUserData, setGoogleUserData] = useState(null);
  const device = useEnhancedDeviceDetection();
  const [googleFormData, setGoogleFormData] = useState({
  gender: '',
  ageConfirmed: false,
  termsAccepted: false,
  marketingConsent: false
});
  
  const { 
    signup, 
    login, 
    resetPassword, 
    signInWithGoogle, 
    completeGoogleRegistration,
    error, 
    clearError, 
    user, 
    userProfile,
    loading 
  } = useAuth();
  
const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/dashboard';

  useEffect(() => {
    if (loading || isLoading) {
      return;
    }

    if (user && userProfile && user.emailVerified && userProfile.is_verified) {
      navigate(from, { replace: true });
    }
  }, [user, userProfile, loading, isLoading, navigate, from]);

  // Track when users reach signup/login page
useEffect(() => {
  if (isSignUp) {
    trackFunnelStep('signup_start', 'reached_page');
  }
}, [isSignUp]);

  useEffect(() => {
  // Track when users reach signup page
  if (isSignUp) {
    trackEvent('signup_page_view', 'Conversion', 'reached_signup');
  }
}, [isSignUp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setSuccess('');
    clearError();
    
    if (isSignUp) {
      // GDPR validation checks
      if (!formData.ageConfirmed) {
        setLocalError('You must confirm you are 18 or older to create an account');
        return;
      }

      if (!formData.termsAccepted) {
        setLocalError('You must accept the Terms & Conditions and Privacy Policy');
        return;
      }

      if (!formData.gender) {
        setLocalError('Please select your gender');
        return;
      }
      
      if (formData.password !== formData.confirmPassword) {
        setLocalError('Passwords do not match');
        return;
      }
      
      if (formData.password.length < 6) {
        setLocalError('Password must be at least 6 characters');
        return;
      }

      setIsLoading(true);

      
      try {

        trackFunnelStep('signup_start', formData.email);
        // Pass consent data to signup
        await signup(
          formData.email, 
          formData.password, 
          formData.email.split('@')[0], 
          formData.gender,
          {
            marketing_consent: formData.marketingConsent,
            terms_accepted: true,
            terms_accepted_at: new Date().toISOString(),
            marketing_consent_at: formData.marketingConsent ? new Date().toISOString() : null
          }
        );

        trackFunnelStep('signup_complete', formData.email);
        
        console.log('Signup successful with consents');
        
        // Clear form state
        setFormData({
          email: '',
          password: '',
          confirmPassword: '',
          gender: '',
          ageConfirmed: false,
          termsAccepted: false,
          marketingConsent: false
        });
      } catch (error) {
        console.error('Signup error:', error);
        setLocalError(getSpecificErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    } else {
      // Login
      setIsLoading(true);
      try {
        await login(formData.email, formData.password);
        console.log('Login successful');
      } catch (error) {
        console.error('Login error:', error);
        setLocalError(getSpecificErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getSpecificErrorMessage = (error) => {
    if (!error) return '';
    
    if (error.code) {
      switch (error.code) {
        case 'auth/user-not-found':
          return 'No account found with this email address. Please check your email or sign up.';
        case 'auth/wrong-password':
          return 'Incorrect password. Please try again or reset your password.';
        case 'auth/invalid-email':
          return 'Please enter a valid email address.';
        case 'auth/user-disabled':
          return 'This account has been disabled. Please contact support.';
        case 'auth/email-already-in-use':
          return 'An account with this email already exists. Please sign in instead.';
        case 'auth/weak-password':
          return 'Password is too weak. Please choose a stronger password.';
        case 'auth/invalid-credential':
          return 'Invalid email or password. Please check your credentials and try again.';
        case 'auth/too-many-requests':
          return 'Too many failed attempts. Please try again later.';
        case 'auth/network-request-failed':
          return 'Network error. Please check your connection and try again.';
        default:
          return error.message || 'Authentication failed. Please try again.';
      }
    }

    if (error.response?.status === 403) {
      return error.response.data.detail || 'Registration is currently unavailable in your region.';
    }
    
    if (error.response?.data?.detail) {
      return error.response.data.detail;
    }
    
    return error.message || 'An unexpected error occurred. Please try again.';
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    if (localError) setLocalError('');
    if (error) clearError();
    if (success) setSuccess('');
  };


// Add these functions to Login.js (before the render return statement):

// Add these functions to Login.js (before the render return statement):

const handleGoogleSignIn = async () => {
  // âœ… VALIDATE CHECKBOXES FIRST (only on signup page)
  if (isSignUp) {
    if (!formData.ageConfirmed) {
      setLocalError('You must confirm you are 18 or older to use Google sign-in');
      return;
    }

    if (!formData.termsAccepted) {
      setLocalError('You must accept the Terms & Conditions to use Google sign-in');
      return;
    }

    if (!formData.gender) {
      setLocalError('Please select your gender before using Google sign-in');
      return;
    }

    // âœ… Store the form data for after Google auth
    sessionStorage.setItem('pendingGoogleSignupData', JSON.stringify({
      gender: formData.gender,
      marketingConsent: formData.marketingConsent
    }));
  }

  setIsLoading(true);
  setLocalError('');
  setSuccess('');
  clearError();
  
  try {
    console.log('Starting Google sign-in...');
    const result = await signInWithGoogle();
    
    console.log('ðŸ“Š Google sign-in result:', result);
    
if (result.requiresAdditionalInfo) {
      // âœ… New user - use stored data instead of showing modal
      console.log('â„¹ï¸ New user detected - completing registration with stored data');
      
      const savedData = JSON.parse(sessionStorage.getItem('pendingGoogleSignupData') || '{}');
      
      await completeGoogleRegistration(result.user, savedData.gender, {
        marketingConsent: savedData.marketingConsent || false
      });
      
      sessionStorage.removeItem('pendingGoogleSignupData');
      setSuccess('Account created successfully! Redirecting...');
      setIsLoading(false);
    } else if (result.success) {
      console.log('âœ… Google sign-in successful - existing user');
      setIsLoading(false);
    }
  } catch (error) {
    console.error('Google sign-in error:', error);
    setLocalError(error.message || 'Failed to sign in with Google');
    sessionStorage.removeItem('pendingGoogleSignupData');
    setIsLoading(false);
  }
};

const handleGoogleInputChange = (e) => {
  const { name, type, checked } = e.target;
  setGoogleFormData({
    ...googleFormData,
    [name]: type === 'checkbox' ? checked : e.target.value
  });
  if (localError) setLocalError('');
};

const handleGoogleGenderSelect = (gender) => {
  setGoogleFormData({
    ...googleFormData,
    gender: gender
  });
  if (localError) setLocalError('');
};

const handleCompleteGoogleRegistration = async (e) => {
  e.preventDefault();
  
  // Validate
  if (!googleFormData.ageConfirmed) {
    setLocalError('You must confirm you are 18 or older');
    return;
  }
  
  if (!googleFormData.termsAccepted) {
    setLocalError('You must accept the Terms & Conditions');
    return;
  }
  
  if (!googleFormData.gender) {
    setLocalError('Please select your gender');
    return;
  }
  
  setIsLoading(true);
  setLocalError('');
  
  try {
    console.log('ðŸ”„ Completing Google registration with data:', googleFormData);
    
    await completeGoogleRegistration(googleFormData.gender, {
      marketingConsent: googleFormData.marketingConsent
    });
    
    console.log('âœ… Google registration completed successfully');
    
    setShowGoogleCompleteModal(false);
    setSuccess('Account created successfully! Redirecting...');
    
    // Clear the form
    setGoogleFormData({
      gender: '',
      ageConfirmed: false,
      termsAccepted: false,
      marketingConsent: false
    });
    
  } catch (error) {
    console.error('âŒ Error completing Google registration:', error);
    setLocalError(error.response?.data?.detail || error.message || 'Failed to complete registration');
  } finally {
    setIsLoading(false);
  }
};

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setLocalError('Please enter your email address first');
      return;
    }

    setIsLoading(true);
    setLocalError('');
    try {
      await resetPassword(formData.email);
      setSuccess('Password reset email sent! Please check your inbox.');
    } catch (error) {
      console.error('Password reset error:', error);
      setLocalError(getSpecificErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenderSelect = (selectedGender) => {
    setFormData({
      ...formData,
      gender: selectedGender
    });
    if (localError) setLocalError('');
  };

// In Login.js - Replace the loading checks (around line 380-420) with this order:

// 1. First check global loading state
if (loading && !showGoogleCompleteModal) {
  return (
    <div className="login-new-container">
      <div className="login-new-card">
        <div className="login-loading-container">
          <div className="login-loading-spinner"></div>
          <p className="login-loading-text">Loading...</p>
        </div>
      </div>
    </div>
  );
}

// 2. ðŸŽ¯ CHECK EMAIL VERIFICATION FIRST (before checking profile)
// This is critical - unverified users should see the verification screen immediately
if (user && !user.emailVerified) {
  return <EmailVerificationStatus />;
}

// 3. Then check if profile is loading (only for verified users)
if (user && !userProfile && !showGoogleCompleteModal) {
  return (
    <div className="login-new-container">
      <div className="login-new-card">
        <div className="login-loading-container">
          <div className="login-loading-spinner"></div>
          <p className="login-loading-text">Loading profile...</p>
        </div>
      </div>
    </div>
  );
}

const displayError = localError || error;

  return (
    <div className="login-new-container">
      <div className="login-new-card">
        <div className="login-new-header">
          <div className="login-new-logo">
            <img src={logo} alt="Earn2Game Logo" />
          </div>
          <h1 className="login-new-title">
            {isAdminLogin ? 'Admin Access' : isSignUp ? 'Join GAME2EARN' : 'Welcome Back'}
          </h1>
          <p className="login-new-subtitle">
            {isAdminLogin ? 'Administrative portal access' : isSignUp ? 'Start earning from gaming today' : 'Play Games Earn Money'}
          </p>
        </div>

        {displayError && (
          <div className="login-message login-error-message">
            <AlertCircle size={20} />
            <span>{displayError}</span>
          </div>
        )}

        {success && (
          <div className="login-message login-success-message">
            <CheckCircle size={20} />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-new-form">
          {/* Email Field */}
          <div className="login-form-group">
            <label className="login-form-label">
              {isSignUp ? 'Email Address' : 'Email'}
            </label>
            <div className="login-input-container">
              <Mail className="login-input-icon-left" size={20} />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="login-form-input"
                placeholder="Enter your email address"
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>
          </div>

          {/* Password Field */}
          <div className="login-form-group">
            <label className="login-form-label">Password</label>
            <div className="login-input-container">
              <Lock className="login-input-icon-left" size={20} />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="login-form-input with-right-icon"
                placeholder="Enter your password"
                required
                disabled={isLoading}
                autoComplete={isSignUp ? "new-password" : "current-password"}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="login-input-icon-right"
                disabled={isLoading}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {isSignUp && (
              <p className="login-demo-info">Password must be at least 6 characters</p>
            )}
          </div>

          {/* Confirm Password Field */}
          {isSignUp && (
            <div className="login-form-group">
              <label className="login-form-label">Confirm Password</label>
              <div className="login-input-container">
                <Lock className="login-input-icon-left" size={20} />
                <input
                  type={showPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="login-form-input"
                  placeholder="Confirm your password"
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>
            </div>
          )}

          {/* Gender Selection */}
          {isSignUp && (
            <div className="login-form-group">
              <label className="login-form-label">Gender</label>
              <div className="login-gender-container">
                <button
                  type="button"
                  onClick={() => handleGenderSelect('male')}
                  className={`login-gender-button ${formData.gender === 'male' ? 'selected' : ''}`}
                  disabled={isLoading}
                >
                  <User size={18} />
                  Male
                </button>
                <button
                  type="button"
                  onClick={() => handleGenderSelect('female')}
                  className={`login-gender-button ${formData.gender === 'female' ? 'selected' : ''}`}
                  disabled={isLoading}
                >
                  <User size={18} />
                  Female
                </button>
              </div>
            </div>
          )}

          {/* GDPR CONSENT CHECKBOXES - ONLY ON SIGNUP */}
          {isSignUp && (
            <div className="login-consent-section">
              {/* Age Verification - REQUIRED */}
              <div className="login-checkbox-group required">
                <label className="login-checkbox-label">
                  <input
                    type="checkbox"
                    name="ageConfirmed"
                    checked={formData.ageConfirmed}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  />
                  <span className="login-checkbox-text">
                    <Shield size={16} className="login-checkbox-icon" />
                    I confirm that I am <strong>18 years or older</strong>
                    <span className="login-required-badge">Required</span>
                  </span>
                </label>
              </div>

              {/* Terms & Privacy - REQUIRED */}
              <div className="login-checkbox-group required">
                <label className="login-checkbox-label">
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  />
                  <span className="login-checkbox-text">
                    <FileText size={16} className="login-checkbox-icon" />
                    I accept the{' '}
                    <a 
                      href="/terms-conditions" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="login-policy-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms & Conditions
                    </a>
                    {' '}and{' '}
                    <a 
                      href="/privacy-policy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="login-policy-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </a>
                    <span className="login-required-badge">Required</span>
                  </span>
                </label>
              </div>

              {/* Marketing Consent - OPTIONAL */}
              <div className="login-checkbox-group optional">
                <label className="login-checkbox-label">
                  <input
                    type="checkbox"
                    name="marketingConsent"
                    checked={formData.marketingConsent}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  <span className="login-checkbox-text">
                    <Mail size={16} className="login-checkbox-icon" />
                    I'd like to receive updates, offers, and gaming tips via email
                    <span className="login-optional-badge">Optional</span>
                  </span>
                </label>
                <p className="login-checkbox-info">
                  You can unsubscribe at any time from your account settings or email links.
                </p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="login-submit-btn"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="login-loading-spinner"></div>
                {isSignUp ? 'Creating Account...' : 'Signing In...'}
              </>
            ) : (
              isSignUp ? 'Create Account' : 'Sign In'
            )}
          </button>
        </form>

        {/* Google Login */}
        {!isAdminLogin && !device.isIOS && (
          <>
            <div className="login-divider">
              <span className="login-divider-text">or continue with</span>
            </div>
            
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="login-google-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {isSignUp && (
              <p className="login-google-disclaimer">
                By signing in with Google, you agree to our Terms and Privacy Policy
              </p>
            )}
          </>
        )}

        {/* Footer Links */}
        <div className="login-footer">
          {!isSignUp && (
            <button
              type="button"
              onClick={handleForgotPassword}
              className="login-link-btn"
              disabled={isLoading}
            >
              Forgot your password?
            </button>
          )}

          {!isAdminLogin && (
            <p className="login-toggle-text">
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
              <span
                onClick={() => navigate(isSignUp ? '/login' : '/signup')}
                className="login-toggle-link"
                role="button"
                tabIndex={0}
              >
                {isSignUp ? 'Sign in here' : 'Sign up now'}
              </span>
            </p>
          )}
          
          <button
            type="button"
            onClick={() => navigate('/')}
            className="login-back-btn"
            disabled={isLoading}
          >
            Back to Home
          </button>
        </div>
      </div>
      {/* Add this right before the final closing </div> in Login.js, after the login-footer div */}

{/* Google Complete Registration Modal */}
{showGoogleCompleteModal && (
  <div className="modal-overlay" onClick={() => {
    setShowGoogleCompleteModal(false);
    setGoogleFormData({ gender: '', ageConfirmed: false, termsAccepted: false, marketingConsent: false });
  }}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <button 
        className="modal-close" 
        onClick={() => {
          setShowGoogleCompleteModal(false);
          setGoogleFormData({ gender: '', ageConfirmed: false, termsAccepted: false, marketingConsent: false });
        }}
      >
        <X size={24} />
      </button>
      
      <div className="modal-header">
        <h2>Complete Your Profile</h2>
        <p>Just a few more details to get started</p>
      </div>
      
      <form onSubmit={handleCompleteGoogleRegistration} className="modal-form">
        {/* Error Display */}
        {localError && (
          <div className="login-alert login-alert-error">
            <AlertCircle size={20} />
            <p>{localError}</p>
          </div>
        )}

        {/* Gender Selection */}
        <div className="login-form-group">
          <label className="login-form-label">Gender</label>
          <div className="login-gender-container">
            <button
              type="button"
              onClick={() => handleGoogleGenderSelect('male')}
              className={`login-gender-button ${googleFormData.gender === 'male' ? 'selected' : ''}`}
              disabled={isLoading}
            >
              <User size={18} />
              Male
            </button>
            <button
              type="button"
              onClick={() => handleGoogleGenderSelect('female')}
              className={`login-gender-button ${googleFormData.gender === 'female' ? 'selected' : ''}`}
              disabled={isLoading}
            >
              <User size={18} />
              Female
            </button>
          </div>
        </div>

        {/* GDPR Consents */}
        <div className="login-consent-section">
          {/* Age Confirmation */}
          <div className="login-checkbox-group required">
            <label className="login-checkbox-label">
              <input
                type="checkbox"
                name="ageConfirmed"
                checked={googleFormData.ageConfirmed}
                onChange={handleGoogleInputChange}
                disabled={isLoading}
                required
              />
              <span className="login-checkbox-text">
                <Shield size={16} className="login-checkbox-icon" />
                I confirm that I am <strong>18 years or older</strong>
                <span className="login-required-badge">Required</span>
              </span>
            </label>
          </div>

          {/* Terms Acceptance */}
          <div className="login-checkbox-group required">
            <label className="login-checkbox-label">
              <input
                type="checkbox"
                name="termsAccepted"
                checked={googleFormData.termsAccepted}
                onChange={handleGoogleInputChange}
                disabled={isLoading}
                required
              />
              <span className="login-checkbox-text">
                <FileText size={16} className="login-checkbox-icon" />
                I accept the{' '}
                <a 
                  href="/terms-conditions" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="login-policy-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms & Conditions
                </a>
                {' '}and{' '}
                <a 
                  href="/privacy-policy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="login-policy-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
                <span className="login-required-badge">Required</span>
              </span>
            </label>
          </div>

          {/* Marketing Consent */}
          <div className="login-checkbox-group optional">
            <label className="login-checkbox-label">
              <input
                type="checkbox"
                name="marketingConsent"
                checked={googleFormData.marketingConsent}
                onChange={handleGoogleInputChange}
                disabled={isLoading}
              />
              <span className="login-checkbox-text">
                <Mail size={16} className="login-checkbox-icon" />
                I'd like to receive updates and offers via email
                <span className="login-optional-badge">Optional</span>
              </span>
            </label>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="login-submit-btn"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="login-loading-spinner"></div>
              Completing Registration...
            </>
          ) : (
            'Complete Registration'
          )}
        </button>
      </form>
    </div>
  </div>
)}
    </div>
  );
};

export default Login;